import { useEffect, useMemo, useState } from "react";
import moment from "moment";
import apiEndpoints from "@store/apiEndpoints";
import errorFunctions from "@store/errorFunctions";

/*
Unsaved edits for the case detail's Save (PATCH /ticket/{id}/, RMAS-128).

The draft lives on TicketCaseDetail rather than in each tab, so edits survive
switching tabs and one Save sends everything. Only edited keys are stored - a
field's shown value is its edit if there is one, else the loaded ticket's - so
a reload (Advance, store location, return address) never wipes pending edits,
and an edit that ends up equal to the saved value stops counting as a change.

Save sends only the changed fields; the backend rejects the whole request with
a 400 keyed by field name if any one is invalid, and those messages are shown
against their inputs. Fields with side effects (status, assignee, service
center, type, due date, refund, replacement) are deliberately not here - they
keep their own endpoints. So does the return address, which the Product tab
saves through ticket/{id}/address/ because that also carries `country`, which
Save doesn't accept. `cosmetic_condition` only takes the keys in
NewCase/options.js COSMETIC_CONDITION_OPTIONS.
*/
const FIELD_KINDS = {
  pickup_address: "text",
  retailer_phone: "text",
  retailer_email: "text",
  primary_email: "text",
  fault_types: "list",
  fault_replicated_in_store: "bool",
  replication_steps: "text",
  cosmetic_condition: "text",
  cosmetic_condition_other: "text",
  water_debris_removed: "bool",
  missing_accessories: "text",
  tracking_number: "text",
  tracking_number_service: "text",
  date_of_return: "date",
  dead_on_arrival: "bool",
  commercial_troubleshooting_details: "text",
  solvup_id: "text",
  inbound_ticket: "text",
  outbound_ticket: "text",
};

const FIELDS = Object.keys(FIELD_KINDS);

// Brings an API value (or an input's value) to one comparable shape per kind.
const normalize = function (kind, value) {
  if (kind === "bool") return value === true || value === false ? value : null;
  if (kind === "list") {
    return (Array.isArray(value) ? value : []).map(function (item) {
      return String(item).trim().toLowerCase();
    });
  }
  if (kind === "date") {
    const when = value ? moment(value) : null;
    return when && when.isValid() ? when.format("YYYY-MM-DD") : "";
  }
  return value == null ? "" : String(value);
};

const same = function (kind, a, b) {
  if (kind === "list") return a.slice().sort().join("|") === b.slice().sort().join("|");
  return a === b;
};

// An emptied date input means "no date", which the API takes as null, not "".
const toPayloadValue = function (kind, value) {
  if (kind === "date") return value || null;
  return value;
};

export default function useTicketDraft(protectedFetch, ticket, onSaved) {
  const [edits, setEdits] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const ticketId = ticket && ticket.id;

  // A different ticket starts from a clean slate.
  useEffect(
    function () {
      setEdits({});
      setFieldErrors({});
      setError(null);
    },
    [ticketId]
  );

  const baseline = useMemo(
    function () {
      const out = {};
      FIELDS.forEach(function (key) {
        out[key] = normalize(FIELD_KINDS[key], ticket ? ticket[key] : null);
      });
      return out;
    },
    [ticket]
  );

  const dirtyKeys = FIELDS.filter(function (key) {
    return key in edits && !same(FIELD_KINDS[key], edits[key], baseline[key]);
  });

  const value = function (key) {
    return key in edits ? edits[key] : baseline[key];
  };

  const set = function (key, next) {
    const normalized = normalize(FIELD_KINDS[key], next);
    setEdits(function (prev) {
      const out = Object.assign({}, prev);
      out[key] = normalized;
      return out;
    });
    if (fieldErrors[key]) {
      setFieldErrors(function (prev) {
        const out = Object.assign({}, prev);
        delete out[key];
        return out;
      });
    }
  };

  // Props for a FormBits control: value, onChange and its 400 message.
  const bind = function (key) {
    return {
      value: value(key),
      onChange: function (next) {
        set(key, next);
      },
      error: fieldErrors[key],
    };
  };

  const discard = function () {
    setEdits({});
    setFieldErrors({});
    setError(null);
  };

  // Resolves true once saved, false on a rejected/failed request.
  const save = function () {
    if (!ticketId || saving || !dirtyKeys.length) return Promise.resolve(false);

    const payload = {};
    dirtyKeys.forEach(function (key) {
      payload[key] = toPayloadValue(FIELD_KINDS[key], edits[key]);
    });

    setSaving(true);
    setError(null);
    setFieldErrors({});
    return protectedFetch(
      apiEndpoints.ticket.update(ticketId),
      "PATCH",
      payload,
      errorFunctions.checkResponseThenJson
    )
      .then(function (res) {
        setEdits(function (prev) {
          const out = Object.assign({}, prev);
          Object.keys(payload).forEach(function (key) {
            delete out[key];
          });
          return out;
        });
        if (onSaved) onSaved(res);
        return true;
      })
      .catch(function (err) {
        const errors = (err && err.errors) || {};
        setFieldErrors(errors);
        // Keys that have no input on screen (or a non-field error) still need
        // to be seen somewhere.
        const unplaced = Object.keys(errors).some(function (key) {
          return !(key in payload);
        });
        setError(
          unplaced || !Object.keys(errors).length
            ? (err && err.message) || "Couldn't save the changes. Please try again."
            : "Some fields couldn't be saved - see the highlighted fields."
        );
        return false;
      })
      .finally(function () {
        setSaving(false);
      });
  };

  return {
    value: value,
    set: set,
    bind: bind,
    dirtyKeys: dirtyKeys,
    isDirty: dirtyKeys.length > 0,
    fieldErrors: fieldErrors,
    error: error,
    saving: saving,
    save: save,
    discard: discard,
  };
}
